// Cross-component signal for Odino's DRAFT-FIRST flow builds (workflows + ETL).
//
// Odino writes workflow/ETL definitions as inert DRAFTS (never activates —
// the operator reviews the graph in the editor and activates/arms it, that
// click IS the confirmation). This tiny store broadcasts two moments from the
// Odino chat stream into the editors so the build is VISIBLE:
//
// - `reveal`: a commit tool succeeded for a target — when the matching editor
//   (auto-navigated by the existing `navigate` side-effect) hydrates that
//   definition, it stagger-animates the nodes in ("the flow draws itself") and
//   auto-loads the freshly written draft version.
//
// The edit-LOCK while a turn runs stays on AppShell's `odinoBusy` (the same
// inert+veil the apps builder uses) — this store only carries the reveal.
//
// Imitates the `capModal.ts` shape: minimal, non-persisted, set imperatively.
import { create } from "zustand";

export type OdinoBuildPlane = "workflow" | "etl";

export interface OdinoReveal {
  plane: OdinoBuildPlane;
  /** The committed entity's id, parsed from the tool's navigate url. */
  id: string;
  /** When the reveal was armed (ms epoch). The editor consumes the reveal ONLY
   *  against query data FRESHER than this — the commit invalidates BOTH the
   *  workflow detail and the currently-open version query, and whichever
   *  refetch lands first re-runs hydration; consuming against the STALE data
   *  left the editor stuck on the previous draft (live bug). */
  armedAt: number;
}

interface OdinoBuildState {
  reveal: OdinoReveal | null;
  setReveal: (r: OdinoReveal) => void;
  /** The editor consumes the reveal exactly once (then animations never re-run). */
  clearReveal: () => void;
}

export const useOdinoBuild = create<OdinoBuildState>((set) => ({
  reveal: null,
  setReveal: (reveal) => set({ reveal }),
  clearReveal: () => set({ reveal: null }),
}));

/** Parse a commit tool's navigate url into a reveal target (the caller stamps
 *  `armedAt` when arming). `/workflows/<id>` → workflow · `/etl/<id>` → etl ·
 *  anything else → null. */
export function revealFromNavigate(
  navigate: string | null | undefined,
): Omit<OdinoReveal, "armedAt"> | null {
  if (!navigate) return null;
  const m = /^\/(workflows|etl)\/([0-9a-fA-F-]{36})$/.exec(navigate);
  if (!m) return null;
  return { plane: m[1] === "workflows" ? "workflow" : "etl", id: m[2] };
}

/** The Odino tools whose success should arm a reveal. */
export const REVEAL_TOOLS = new Set(["commit_workflow", "commit_etl"]);
